/* ============================================================================
   Vertical thumbnail slider (product page, left/right thumbnail layout)

   This file lives OUTSIDE the Vue subtree on purpose. The gallery is rendered
   inside `.product-form-vue`, a Vue 2 inline-template component that STRIPS any
   <style>/<script> tags it finds in its template and rebuilds its DOM on mount
   (~2s after load) and on every variant change. An external, deferred script can
   never be removed by Vue, and its listeners/observers keep running across every
   re-render.

   Strategy:
     - Anchor all runtime state on the stable section `[data-wetheme-section-type]`
       (an ANCESTOR of the Vue mount point, which Vue never touches) - NOT on
       `.product-form-vue`, which Vue replaces.
     - One debounced MutationObserver per section re-runs the idempotent enhance
       pass after the initial mount AND after every re-render.
     - Native vertical scroll drives the rail; arrows call scrollBy(). The visual
       clamp is pure CSS (see the section stylesheet); JS only refines the exact
       pixel height and wires the arrows.
   ============================================================================ */
(function () {
  var SEL = '[data-pp-thumb-slider]';
  var VP_CLASS = 'pp-thumb-viewport';

  function num(v, d) { v = parseInt(v, 10); return isNaN(v) ? d : v; }
  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Vue MOUNTS ON and REPLACES `.product-form-vue`, so state parked there is destroyed the
  // moment Vue takes over. The section wrapper (`[data-wetheme-section-type]`) is an ANCESTOR
  // of the mount point, so Vue never touches it. We persist height + scroll position here and
  // it survives every gallery rebuild; because it is an ancestor, the `--pp-thumb-h` CSS var
  // also inherits down to the viewport.
  function stableRoot(slider) {
    return slider.closest('[data-wetheme-section-type]')
        || slider.closest('.quick-view')
        || document.documentElement;
  }

  /* ---- Observer management -------------------------------------------------------------
     One MutationObserver per stable section. Because the section is never replaced, the
     observer keeps firing across Vue's initial mount AND every later re-render (variant
     change). `apply()` turns the observers OFF while our own code mutates the DOM and back ON
     afterwards, so our writes can never retrigger it (anti-loop: disconnect -> mutate -> reconnect). */
  var OBS_OPTS = { childList: true, subtree: true };
  var observers = [];      // [{ mo: MutationObserver, target: Element }]
  var applying = false;

  function pauseObservers() { observers.forEach(function (o) { o.mo.disconnect(); }); }
  function resumeObservers() { observers.forEach(function (o) { o.mo.observe(o.target, OBS_OPTS); }); }

  function apply(fn) {
    if (applying) { fn(); return; }   // reentrant call: already inside a paused window
    applying = true;
    pauseObservers();
    try { fn(); } finally { resumeObservers(); applying = false; }
  }

  function alreadyWatching(root) {
    for (var i = 0; i < observers.length; i++) { if (observers[i].target === root) return true; }
    return false;
  }

  // Attach an observer to each slider's stable section (idempotent - safe to call repeatedly).
  function ensureObservers() {
    if (typeof MutationObserver === 'undefined') return;
    Array.prototype.forEach.call(document.querySelectorAll(SEL), function (slider) {
      var root = stableRoot(slider);
      if (!root || alreadyWatching(root)) return;
      var debounce;
      var mo = new MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(refreshAll, 80);
      });
      mo.observe(root, OBS_OPTS);
      observers.push({ mo: mo, target: root });
    });
  }

  function viewportOf(slider) { return slider.querySelector('.pp-thumb-viewport'); }

  function firstThumbHeight(slider) {
    var t = slider.querySelector('.pp-thumb-track .product-medias__thumbnail');
    return t ? t.getBoundingClientRect().height : 0;
  }

  // Write the exact "N thumbnails tall" pixel height into --pp-thumb-h on the stable section;
  // the `.pp-thumb-viewport--clip` rule reads it, so the height holds across re-renders with no
  // jump. When it is not yet set, the pure-CSS aspect-ratio fallback keeps the rail clamped.
  function sizeAll() {
    Array.prototype.forEach.call(document.querySelectorAll(SEL), function (slider) {
      if (!slider.querySelector('.pp-thumb-viewport--clip')) return; // few images -> show all
      var h = firstThumbHeight(slider);
      if (!h) return; // images not laid out yet; a later event re-runs this
      var gap = num(slider.getAttribute('data-pp-space'), 12);
      var visible = num(slider.getAttribute('data-pp-visible'), 5);
      stableRoot(slider).style.setProperty('--pp-thumb-h', Math.round(h * visible + gap * (visible - 1)) + 'px');
    });
  }

  // Enable/disable arrows purely from the LIVE scroll position, and persist the position so a
  // Vue re-render can't lose it. Never stores a stale "max travel" that could get stuck.
  function syncArrows(slider) {
    var vp = viewportOf(slider);
    if (!vp) return;
    var max = vp.scrollHeight - vp.clientHeight;
    var scrollable = max > 2;
    var top = vp.scrollTop;
    var prev = slider.querySelector('.pp-thumb-nav--prev');
    var next = slider.querySelector('.pp-thumb-nav--next');
    slider.classList.toggle('pp-thumb-slider--static', !scrollable);
    if (prev) prev.disabled = !scrollable || top <= 1;
    if (next) next.disabled = !scrollable || top >= max - 1;
    stableRoot(slider).setAttribute('data-pp-scroll', Math.round(top));
  }

  function savedScroll(slider) {
    var v = parseFloat(stableRoot(slider).getAttribute('data-pp-scroll'));
    return isNaN(v) ? 0 : v;
  }

  // Arrow click -> native scrollBy, which reads the current scroll state (no stale numbers).
  function stepScroll(slider, dir) {
    var vp = viewportOf(slider);
    if (!vp) return;
    var h = firstThumbHeight(slider);
    var gap = num(slider.getAttribute('data-pp-space'), 12);
    var visible = num(slider.getAttribute('data-pp-visible'), 5);
    var unit = h ? (h + gap) : (vp.clientHeight * 0.85);
    // Move by a page minus one row, so one thumbnail overlaps for context.
    var amount = unit * Math.max(1, visible - 1);
    vp.scrollBy({ top: dir * amount, behavior: reduced() ? 'auto' : 'smooth' });
    // scrollBy fires a scroll event, which refreshes the arrow state.
  }

  // Highlight the clicked thumbnail and, if it is out of view, bring it into the window.
  function setActive(thumb) {
    var slider = thumb.closest(SEL);
    if (!slider) return;
    Array.prototype.forEach.call(slider.querySelectorAll('.pp-thumb--active'), function (el) {
      el.classList.remove('pp-thumb--active');
    });
    thumb.classList.add('pp-thumb--active');
    var vp = viewportOf(slider);
    if (!vp) return;
    var top = thumb.offsetTop, bottom = top + thumb.offsetHeight;
    var behavior = reduced() ? 'auto' : 'smooth';
    if (top < vp.scrollTop) vp.scrollTo({ top: top, behavior: behavior });
    else if (bottom > vp.scrollTop + vp.clientHeight) vp.scrollTo({ top: bottom - vp.clientHeight, behavior: behavior });
  }

  function initActive(slider) {
    if (slider.querySelector('.pp-thumb--active')) return;
    var first = slider.querySelector('.pp-thumb-track .product-medias__thumbnail');
    if (first) first.classList.add('pp-thumb--active');
  }

  // ResizeObserver fires exactly when a thumbnail image loads and the track grows - this is
  // what re-enables arrows that were sized before the lazyloaded images had any height.
  var ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(function () { refreshAll(); }) : null;

  // The idempotent "enhance" pass: safe to call any number of times. It re-measures the rail,
  // re-highlights the active thumb, restores the saved scroll position, re-observes the (new)
  // track, and re-syncs the arrows - so calling it after every Vue re-render fully rebuilds the
  // slider's runtime state. Wrapped in apply() so our own DOM writes never retrigger the
  // observers that call us (anti-loop: disconnect -> mutate -> reconnect).
  function refreshAll() {
    ensureObservers();   // pick up any slider inserted after load (e.g. quick view)
    apply(function () {
      sizeAll();
      Array.prototype.forEach.call(document.querySelectorAll(SEL), function (slider) {
        initActive(slider);
        var vp = viewportOf(slider);
        if (!vp) return;
        // Restore the position a Vue re-render may have reset to 0.
        var want = savedScroll(slider);
        if (want && Math.abs(vp.scrollTop - want) > 1) vp.scrollTop = want;
        // (Re)observe the current track so image loads keep re-syncing after each re-render.
        var track = slider.querySelector('.pp-thumb-track');
        if (ro && track && !track.__ppRO) { ro.observe(track); track.__ppRO = true; }
        syncArrows(slider);
      });
    });
  }

  /* ---- Delegated events: bound once on document, so they survive every Vue re-render. ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.pp-thumb-nav');
    if (btn) {
      var slider = btn.closest(SEL);
      if (slider) { e.preventDefault(); stepScroll(slider, btn.classList.contains('pp-thumb-nav--next') ? 1 : -1); }
      return;
    }
    // Do NOT preventDefault here - let the theme's own v-on:click="onMediaActivate" switch the
    // main image. We only add the active-thumb highlight + scroll-into-view.
    var thumb = e.target.closest && e.target.closest(SEL + ' .product-medias__thumbnail');
    if (thumb) setActive(thumb);
  });

  // Live arrow state as the rail scrolls (scroll does not bubble -> capture phase).
  document.addEventListener('scroll', function (e) {
    var vp = e.target;
    if (vp && vp.nodeType === 1 && vp.classList && vp.classList.contains(VP_CLASS)) {
      var slider = vp.closest(SEL);
      if (slider) syncArrows(slider);
    }
  }, true);

  // A thumbnail image finished loading. lazysizes swaps src (an attribute change, which
  // childList observers miss), so listen for the image 'load' itself (capture: it doesn't
  // bubble) and re-measure + re-enable the arrows.
  document.addEventListener('load', function (e) {
    var img = e.target;
    if (img && img.tagName === 'IMG' && img.closest && img.closest(SEL)) refreshAll();
  }, true);

  function runRetries() {
    [0, 100, 300, 600, 1200, 2500].forEach(function (d) { setTimeout(refreshAll, d); });
  }

  // Attach the section observers as early as possible so Vue's initial mount is caught.
  ensureObservers();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runRetries);
  } else {
    runRetries();
  }
  window.addEventListener('load', refreshAll);
  // Theme editor reloads the whole section (new nodes): re-attach observers, then re-measure.
  document.addEventListener('shopify:section:load', function () { ensureObservers(); runRetries(); });

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(refreshAll, 150);
  });
})();
(function waitForVue() {
  var vue = document.querySelector('.product-single-wrapper')?.__vue__;
  if (!vue || !vue.mediaWrapperById) {
    return setTimeout(waitForVue, 200);
  }

  // Fix sectionId for all existing wrappers
  var firstTemplate = document.querySelector('[id^="media-template-"]');
  if (!firstTemplate) return setTimeout(waitForVue, 200);
  var realSectionId = firstTemplate.id.replace('media-template-', '').replace(/-\d+$/, '');

  vue.mediaWrapperById.forEach(function(wrapper) {
    wrapper.sectionId = realSectionId;
  });

  document.addEventListener('click', function(e) {
  var btn = e.target.closest('.product-medias__icon');
  if (!btn) return;
  var slide = btn.closest('.swiper-slide');
  if (!slide) return;
  var mediaDiv = slide.querySelector('.product-medias__media[data-media-type="video"]');
  if (!mediaDiv) return;
  var mediaId = mediaDiv.dataset.mediaId;
  var tmpl = document.querySelector('#media-template-' + mediaId);
  // Find template by media ID suffix
  if (!tmpl) {
    tmpl = document.querySelector('[id$="-' + mediaId + '"]');
  }
  if (!tmpl) return;
  e.stopImmediatePropagation();
  var content = tmpl.content.cloneNode(true);
  var video = content.querySelector('video');
  mediaDiv.innerHTML = '';
  mediaDiv.appendChild(content);
  if (video) mediaDiv.querySelector('video').play();
});


})();
